import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { checkSubscription } from '../lib/stripe-client'

interface Subscription {
  id: string
  status: string
  isTrialing: boolean
  trialEnd: string | null
  currentPeriodEnd: string
  cancelAtPeriodEnd: boolean
  priceId: string
}

interface SubscriptionContextType {
  subscription: Subscription | null
  hasActiveSubscription: boolean
  isLoading: boolean
  error: string | null
  refreshSubscription: () => Promise<void>
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined)

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refreshSubscription = async () => {
    console.log('[SubscriptionContext] Starting refresh...')
    setIsLoading(true)
    setError(null)

    try {
      // Get current session
      const { data: { session } } = await supabase.auth.getSession()
      console.log('[SubscriptionContext] Session:', session?.user?.email || 'No session')

      if (!session) {
        console.log('[SubscriptionContext] No session, setting no subscription')
        setSubscription(null)
        setHasActiveSubscription(false)
        setIsLoading(false)
        return
      }

      // Check subscription status
      console.log('[SubscriptionContext] Checking subscription...')
      const result = await checkSubscription(session.access_token)
      console.log('[SubscriptionContext] Result:', result)

      setHasActiveSubscription(result.hasSubscription)
      setSubscription(result.subscription || null)
      console.log('[SubscriptionContext] State updated - hasActiveSubscription:', result.hasSubscription)
    } catch (err) {
      console.error('[SubscriptionContext] Error fetching subscription:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch subscription')
      setHasActiveSubscription(false)
      setSubscription(null)
    } finally {
      setIsLoading(false)
      console.log('[SubscriptionContext] Refresh complete')
    }
  }

  // Initial load and auth state changes
  useEffect(() => {
    refreshSubscription()

    // Listen for auth state changes
    const setupAuthListener = async () => {
      const { data } = supabase.auth.onAuthStateChange((event: string) => {
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          refreshSubscription()
        } else if (event === 'SIGNED_OUT') {
          setSubscription(null)
          setHasActiveSubscription(false)
          setIsLoading(false)
        }
      })

      return data.subscription
    }

    let authSubscription: any
    setupAuthListener().then((sub) => {
      authSubscription = sub
    })

    return () => {
      authSubscription?.unsubscribe()
    }
  }, [])

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        hasActiveSubscription,
        isLoading,
        error,
        refreshSubscription,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  )
}

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext)
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider')
  }
  return context
}
